import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { StipUploadModal } from '../components/StipUploadModal';
import { getDeal, updateDeal, createDeal, uploadStip, verifyStip, getActivityLogs, aiAnalyze, scheduleInterview, completeInterview, sendInterviewReminder, generateInterviewMessage, deleteDeal, sendCustomerSMS, sendCustomerEmail, getDealNotes, createDealNote, updateDealNote, deleteDealNote, addStipToDeal, removeStipFromDeal, bulkUpdateStipStatus, toggleDealFunding, archiveDeal, unarchiveDeal, unwindDeal, reverseUnwindDeal, getDefaultStipulations, rescheduleInterview, getUsers, togglePinNote, getSettings, getLenders } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  ArrowLeft,
  Upload,
  Check,
  X,
  Clock,
  Phone,
  Shield,
  Activity,
  AlertTriangle,
  Calendar as CalendarIcon,
  Send,
  Sparkles,
  Loader2,
  CheckCircle,
  FileText,
  Mail,
  Eye,
  Download,
  Trash2,
  MessageSquare,
  StickyNote,
  Edit3,
  Save,
  RefreshCw,
  Copy,
  AlertCircle,
  Plus,
  CheckSquare,
  Square,
  DollarSign,
  Archive,
  ArchiveRestore,
  ToggleLeft,
  ToggleRight,
  Pin,
  XCircle,
} from 'lucide-react';

export const DealDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = id === 'new';
  const canDelete = user?.role === 'admin' || user?.role === 'general_manager';
  
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [activities, setActivities] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const [interviewDate, setInterviewDate] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [generatedMessageType, setGeneratedMessageType] = useState('');
  const [uploadModal, setUploadModal] = useState({ open: false, stipName: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sendingSMS, setSendingSMS] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Deal Notes state
  const [dealNotes, setDealNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [newNoteType, setNewNoteType] = useState('internal');
  const [noteFilter, setNoteFilter] = useState('all');
  const [savingNote, setSavingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');

  // Aging thresholds from settings
  const [thresholds, setThresholds] = useState({ warning: 3, critical: 10 });
  const [lendersList, setLendersList] = useState([]);  
  // Filtered notes based on selected filter
  const filteredNotes = dealNotes.filter(note => {
    if (noteFilter === 'all') return true;
    if (noteFilter === 'internal') return note.note_type === 'internal' || !note.note_type;
    if (noteFilter === 'admin_alert') return note.note_type === 'admin_alert';
    return note.note_type === noteFilter;
  });
  
  // AI Interview Prep state
  const [aiPrepContent, setAiPrepContent] = useState('');
  const [aiPrepOutdated, setAiPrepOutdated] = useState(false);
  
  // Check if user can edit notes (GM, Admin, Finance Director, Sales)
  const canEditNotes = ['admin', 'general_manager', 'finance_director', 'sales_manager', 'salesperson'].includes(user?.role);
  const [editingContact, setEditingContact] = useState(false);
  const [editingDealNumber, setEditingDealNumber] = useState(false);
  const [dealNumberEdit, setDealNumberEdit] = useState('');
  const [editingOverview, setEditingOverview] = useState(false);
  const [overviewForm, setOverviewForm] = useState({});
  const [savingOverview, setSavingOverview] = useState(false);
  const [contactForm, setContactForm] = useState({ phone: '', email: '' });
  const [savingContact, setSavingContact] = useState(false);

  // Stipulation Management state
  const [newStipName, setNewStipName] = useState('');
  const [addingStip, setAddingStip] = useState(false);
  const [removingStip, setRemovingStip] = useState(null);
  const [selectedStips, setSelectedStips] = useState([]);
  const [recentlyRemoved, setRecentlyRemoved] = useState(null);
  const [undoTimeout, setUndoTimeout] = useState(null);

  // Deal Lifecycle state
  const [togglingFunding, setTogglingFunding] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [unarchiving, setUnarchiving] = useState(false);
  const [unwindModalOpen, setUnwindModalOpen] = useState(false);
  const [unwindReason, setUnwindReason] = useState('');
  const [unwindReasonOther, setUnwindReasonOther] = useState('');
  const [unwinding, setUnwinding] = useState(false);
  const [reversingUnwind, setReversingUnwind] = useState(false);
  const [usersList, setUsersList] = useState([]);

  // Form state for new deal
  const [formData, setFormData] = useState({
    customer_first_name: '',
    customer_last_name: '',
    deal_number: '',
    vehicle_info: '',
    stock_number: '',
    sales_person: '',
    closer: '',
    finance_manager: user?.role === 'finance_manager' ? (user?.name || '') : '',
    desk_manager: '',
    amount_financed: 0,
    lender: '',
    pending_funds: 0,
    comments: '',
    deal_date: new Date().toISOString().split('T')[0],
    customer_phone: '',
    customer_email: '',
  });

  // Explicit interview required choice: null = not chosen, 'yes', 'no'
  const [interviewChoice, setInterviewChoice] = useState(null);
  // Explicit "No Stipulations" checkbox
  const [stipNoneSelected, setStipNoneSelected] = useState(false);

  // New Deal - Stipulation selection state
  const [predefinedStips, setPredefinedStips] = useState([]);
  const [formSelectedStips, setFormSelectedStips] = useState(new Set());
  const [formCustomStips, setFormCustomStips] = useState([]);
  const [customStipInput, setCustomStipInput] = useState('');
  const [stipsExpanded, setStipsExpanded] = useState(true);

  useEffect(() => {
    // Always load users list for dropdowns (finance manager, sales person)
    getUsers().then(res => {
      setUsersList((res.data || []).filter(u => u.is_active));
    }).catch(() => {});

    // Load aging thresholds from settings
    getSettings().then(res => {
      const s = res.data;
      if (s) {
        setThresholds({
          warning: s.day_warning_threshold ?? 3,
          critical: s.day_critical_threshold ?? 10,
        });
      }
    }).catch(() => {});

    // Load lenders list for dropdown
    getLenders().then(res => {
      setLendersList(res.data?.lenders || []);
    }).catch(() => {});

    if (!isNew) {
      loadDeal();
      loadActivities();
      loadDealNotes();
    } else {
      getDefaultStipulations().then(res => {
        setPredefinedStips(res.data.stipulations || []);
      }).catch(() => {});
    }
  }, [id, isNew]);

  // Real-time updates via WebSocket
  const ws = useWebSocket();
  useEffect(() => {
    if (!ws || isNew) return;
    const events = [
      'deal_updated', 'deal_funded', 'deal_unfunded', 'deal_archived', 'deal_unarchived',
      'stip_verified', 'stip_uploaded', 'note_created', 'note_updated', 'note_deleted', 'note_pinned',
      'escalation_action', 'notification_created',
    ];
    const unsubs = events.map(evt => ws.subscribe(evt, (data) => {
      // Only refresh if event is for this deal (or we can't tell)
      if (!data?.deal_id || data.deal_id === id) {
        loadDeal();
        loadDealNotes();
      }
    }));
    return () => unsubs.forEach(fn => fn());
  }, [ws, id, isNew]);

  const loadDeal = async () => {
    try {
      const res = await getDeal(id);
      setDeal(res.data);
      setAiPrepOutdated(res.data.interview_prep_outdated || false);
    } catch (err) {
      toast.error('Failed to load deal');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const loadActivities = async () => {
    try {
      const res = await getActivityLogs({ deal_id: id, limit: 50 });
      setActivities(res.data);
    } catch (err) {
      console.error('Failed to load activities', err);
    }
  };

  const loadDealNotes = async () => {
    try {
      const res = await getDealNotes(id);
      setDealNotes(res.data || []);
    } catch (err) {
      console.error('Failed to load deal notes', err);
    }
  };

  const handleSaveNote = async () => {
    if (!newNoteText.trim()) return;
    setSavingNote(true);
    try {
      await createDealNote(id, newNoteText.trim(), newNoteType);
      toast.success(`${newNoteType === 'internal' ? 'Internal' : 'Lender-facing'} note saved`);
      setNewNoteText('');
      loadDealNotes();
      if (newNoteType === 'internal') {
        setAiPrepOutdated(true); // Mark AI prep as outdated only for internal notes
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleUpdateNote = async (noteId) => {
    if (!editNoteText.trim()) return;
    setSavingNote(true);
    try {
      await updateDealNote(id, noteId, editNoteText.trim());
      toast.success('Note updated');
      setEditingNoteId(null);
      setEditNoteText('');
      loadDealNotes();
      setAiPrepOutdated(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    try {
      await deleteDealNote(id, noteId);
      toast.success('Note deleted');
      loadDealNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete note');
    }
  };

  const handlePinNote = async (noteId) => {
    try {
      await togglePinNote(id, noteId);
      loadDealNotes();
    } catch (err) {
      toast.error('Failed to pin note');
    }
  };

  const handleCopyAiPrep = () => {
    navigator.clipboard.writeText(aiPrepContent || aiResponse);
    toast.success('Copied to clipboard');
  };

  // ==================== STIPULATION MANAGEMENT HANDLERS ====================
  const handleAddStip = async () => {
    if (!newStipName.trim()) return;
    
    setAddingStip(true);
    try {
      await addStipToDeal(id, newStipName.trim(), true);
      toast.success(`Stipulation "${newStipName}" added`);
      setNewStipName('');
      loadDeal(); // Refresh deal data
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add stipulation');
    } finally {
      setAddingStip(false);
    }
  };

  const handleRemoveStip = async (stipName) => {
    // Clear any existing undo timeout
    if (undoTimeout) {
      clearTimeout(undoTimeout);
    }
    
    setRemovingStip(stipName);
    try {
      await removeStipFromDeal(id, stipName);
      
      // Set up undo window
      setRecentlyRemoved({ name: stipName, timestamp: Date.now() });
      const timeout = setTimeout(() => {
        setRecentlyRemoved(null);
      }, 5000); // 5 second undo window
      setUndoTimeout(timeout);
      
      toast.success(
        <div className="flex items-center gap-2">
          <span>Stipulation removed</span>
          <button 
            onClick={() => handleUndoRemove(stipName)}
            className="text-blue-500 underline font-semibold"
          >
            Undo
          </button>
        </div>,
        { duration: 5000 }
      );
      
      loadDeal(); // Refresh deal data
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove stipulation');
    } finally {
      setRemovingStip(null);
    }
  };

  const handleUndoRemove = async (stipName) => {
    if (undoTimeout) {
      clearTimeout(undoTimeout);
    }
    setRecentlyRemoved(null);
    
    try {
      await addStipToDeal(id, stipName, false);
      toast.success('Stipulation restored');
      loadDeal();
    } catch (err) {
      toast.error('Failed to restore stipulation');
    }
  };

  const handleSelectAllStips = () => {
    const allStipNames = stips.filter(s => s.status !== 'verified').map(s => s.name);
    setSelectedStips(allStipNames);
  };

  const handleDeselectAllStips = () => {
    setSelectedStips([]);
  };

  const handleToggleStipSelection = (stipName) => {
    setSelectedStips(prev => 
      prev.includes(stipName) 
        ? prev.filter(n => n !== stipName)
        : [...prev, stipName]
    );
  };

  const handleBulkVerify = async () => {
    if (selectedStips.length === 0) return;
    
    try {
      await bulkUpdateStipStatus(id, selectedStips, 'verified');
      toast.success(`${selectedStips.length} stipulations verified`);
      setSelectedStips([]);
      loadDeal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update stipulations');
    }
  };

  const handleBulkReset = async () => {
    if (selectedStips.length === 0) return;
    
    try {
      await bulkUpdateStipStatus(id, selectedStips, 'missing');
      toast.success(`${selectedStips.length} stipulations reset to missing`);
      setSelectedStips([]);
      loadDeal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset stipulations');
    }
  };

  // ==================== DEAL LIFECYCLE HANDLERS ====================
  const handleToggleFunding = async () => {
    const newFundingStatus = !deal.funding_status;
    setTogglingFunding(true);
    try {
      await toggleDealFunding(id, newFundingStatus);
      toast.success(newFundingStatus ? 'Deal marked as FUNDED' : 'Deal marked as NOT FUNDED');
      loadDeal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update funding status');
    } finally {
      setTogglingFunding(false);
    }
  };

  const handleArchiveDeal = async () => {
    if (!window.confirm('Archive this deal? It will be removed from the active pipeline.')) return;
    setArchiving(true);
    try {
      await archiveDeal(id);
      toast.success('Deal archived successfully');
      navigate('/'); // Go back to dashboard
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive deal');
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchiveDeal = async () => {
    setUnarchiving(true);
    try {
      await unarchiveDeal(id);
      toast.success('Deal restored to active pipeline');
      loadDeal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unarchive deal');
    } finally {
      setUnarchiving(false);
    }
  };

  const canUnwind = user?.role === 'admin' || user?.role === 'general_manager' || user?.role === 'finance_manager';
  const canReverseUnwind = user?.role === 'admin';

  const openUnwindModal = () => {
    setUnwindReason('');
    setUnwindReasonOther('');
    setUnwindModalOpen(true);
  };

  const closeUnwindModal = () => {
    if (unwinding) return;
    setUnwindModalOpen(false);
    setUnwindReason('');
    setUnwindReasonOther('');
  };

  const handleConfirmUnwind = async () => {
    if (!unwindReason) {
      toast.error('Please select a reason');
      return;
    }
    if (unwindReason === 'Other' && !unwindReasonOther.trim()) {
      toast.error('Please enter a custom reason');
      return;
    }
    setUnwinding(true);
    try {
      await unwindDeal(id, unwindReason, unwindReason === 'Other' ? unwindReasonOther.trim() : null);
      toast.success('Deal unwound and moved to Unwinds');
      setUnwindModalOpen(false);
      setUnwindReason('');
      setUnwindReasonOther('');
      loadDeal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unwind deal');
    } finally {
      setUnwinding(false);
    }
  };

  const handleReverseUnwind = async () => {
    if (!window.confirm('Reverse this unwind and return the deal to the ACTIVE pipeline?')) return;
    setReversingUnwind(true);
    try {
      await reverseUnwindDeal(id);
      toast.success('Deal restored to ACTIVE');
      loadDeal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reverse unwind');
    } finally {
      setReversingUnwind(false);
    }
  };

  const handleSaveDeal = async () => {
    if (isNew) {
      // Validate required fields
      if (!formData.customer_first_name?.trim()) {
        toast.error('Customer First Name is required');
        return;
      }
      if (!formData.customer_last_name?.trim()) {
        toast.error('Customer Last Name is required');
        return;
      }
      if (!formData.deal_number?.trim()) {
        toast.error('Deal Number is required');
        return;
      }
      if (!formData.closer?.trim()) {
        toast.error('Closer is required');
        return;
      }
      if (interviewChoice === null) {
        toast.error('Please select Yes or No for Interview Required');
        return;
      }
      // Validate stipulations: must pick stips OR check "None"
      const hasStips = formSelectedStips.size > 0 || formCustomStips.length > 0;
      if (!hasStips && !stipNoneSelected) {
        toast.error('Stipulation selection is required. Select stipulations or choose "None Required".');
        return;
      }
    }

    try {
      if (isNew) {
        const payload = { ...formData, interview_required: interviewChoice === 'yes' };
        // Build stipulations array from selected predefined + custom
        const stipulations = [];
        formSelectedStips.forEach(name => {
          stipulations.push({ name, is_custom: false });
        });
        formCustomStips.forEach(name => {
          stipulations.push({ name, is_custom: true });
        });
        if (stipulations.length > 0) {
          payload.stipulations = stipulations;
        } else {
          payload.stipulations = [];
        }
        payload.stip_none_selected = stipNoneSelected;
        const res = await createDeal(payload);
        toast.success('Deal created successfully');
        navigate(`/deal/${res.data.id}`);
      } else {
        await updateDeal(id, formData);
        toast.success('Deal updated');
        loadDeal();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save deal');
    }
  };

  const handleDeleteDeal = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }
    
    setDeleting(true);
    try {
      const res = await deleteDeal(id);
      toast.success(res.data.message || 'Deal deleted successfully');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete deal');
      setDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleStipUpload = (stipName) => {
    // Open the upload modal
    setUploadModal({ open: true, stipName });
  };

  const handleUploadSuccess = () => {
    loadDeal();
    loadActivities();
  };

  const handleViewFile = (stip) => {
    if (stip.file_id) {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('cit_token');
      // Open in new tab with token for authentication
      window.open(`${backendUrl}/api/stips/files/${stip.file_id}?token=${token}`, '_blank');
    }
  };

  const handleDownloadFile = (stip) => {
    if (stip.file_id) {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('cit_token');
      // Create a temporary link to download with auth token
      const link = document.createElement('a');
      link.href = `${backendUrl}/api/stips/files/${stip.file_id}?download=true&token=${token}`;
      link.download = stip.original_filename || `${stip.name}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Downloading ${stip.original_filename || stip.name}`);
    }
  };

  const handleStipVerify = async (stipName) => {
    try {
      await verifyStip(id, stipName);
      toast.success(`${stipName} verified`);
      loadDeal();
      loadActivities();
    } catch (err) {
      toast.error('Verification failed');
    }
  };

  const handleAIAnalysis = async (action) => {
    setAiLoading(true);
    setAiResponse('');
    if (action === 'interview_prep') {
      setAiPrepContent('');
    }
    try {
      const lang = deal?.language_compliance === 'Spanish' ? 'Spanish' : deal?.language_compliance === 'Bilingual' ? 'Spanish' : 'English';
      const res = await aiAnalyze(id, action, null, lang);
      const response = res.data.response || 'No analysis available';
      setAiResponse(response);
      if (action === 'interview_prep') {
        setAiPrepContent(response);
        setAiPrepOutdated(false);
      }
    } catch (err) {
      toast.error('AI analysis failed');
    } finally {
      setAiLoading(false);
    }
  };

  const handleScheduleInterview = async () => {
    if (!interviewDate) {
      toast.error('Please select a date and time');
      return;
    }
    try {
      await scheduleInterview({ deal_id: id, scheduled_time: interviewDate.toISOString() });
      toast.success('Interview scheduled');
      loadDeal();
      loadActivities();
    } catch (err) {
      toast.error('Failed to schedule interview');
    }
  };

  const handleRescheduleInterview = async () => {
    if (!rescheduleDate) {
      toast.error('Please select a new date and time');
      return;
    }
    setRescheduling(true);
    try {
      const res = await rescheduleInterview(id, { scheduled_time: rescheduleDate.toISOString() });
      const data = res.data;
      let msg = 'Interview rescheduled';
      if (data.sms_sent && data.email_sent) msg += ' — SMS & email sent to customer';
      else if (data.sms_sent) msg += ' — SMS sent to customer';
      else if (data.email_sent) msg += ' — Email sent to customer';
      toast.success(msg);
      setRescheduleDate(null);
      loadDeal();
      loadActivities();
    } catch (err) {
      toast.error('Failed to reschedule interview');
    } finally {
      setRescheduling(false);
    }
  };

  const handleCompleteInterview = async () => {
    try {
      await completeInterview(id);
      toast.success('Interview marked as completed');
      loadDeal();
      loadActivities();
    } catch (err) {
      toast.error('Failed to complete interview');
    }
  };

  const handleStatusUpdate = async (field, value) => {
    try {
      await updateDeal(id, { [field]: value });
      toast.success('Status updated');
      loadDeal();
      loadActivities();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleSendReminder = async () => {
    try {
      await sendInterviewReminder(id);
      toast.success('Reminder sent');
      loadDeal();
      loadActivities();
    } catch (err) {
      toast.error('Failed to send reminder');
    }
  };

  const handleGenerateMessage = async (type) => {
    setMessageLoading(true);
    try {
      const lang = deal?.language_compliance === 'Spanish' ? 'Spanish' : deal?.language_compliance === 'Bilingual' ? 'Spanish' : 'English';
      const res = await generateInterviewMessage(id, type, lang);
      setGeneratedMessage(res.data.message || '');
      setGeneratedMessageType(type);
    } catch (err) {
      toast.error('Failed to generate message');
    } finally {
      setMessageLoading(false);
    }
  };

  const handleSendSMS = async () => {
    if (!generatedMessage) {
      toast.error('Please generate a message first');
      return;
    }
    setSendingSMS(true);
    try {
      const res = await sendCustomerSMS(id, generatedMessage, generatedMessageType);
      if (res.data.success) {
        toast.success(res.data.message);
        loadActivities();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSendingSMS(false);
    }
  };

  const handleSendEmail = async () => {
    if (!generatedMessage) {
      toast.error('Please generate a message first');
      return;
    }
    setSendingEmail(true);
    try {
      const res = await sendCustomerEmail(id, generatedMessage, generatedMessageType);
      if (res.data.success) {
        toast.success(res.data.message);
        loadActivities();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleEditContact = () => {
    setContactForm({
      phone: deal?.customer_phone || '',
      email: deal?.customer_email || ''
    });
    setEditingContact(true);
  };

  const handleSaveContact = async () => {
    setSavingContact(true);
    try {
      await updateDeal(id, {
        customer_phone: contactForm.phone,
        customer_email: contactForm.email
      });
      toast.success('Contact info updated');
      setEditingContact(false);
      loadDeal();
    } catch (err) {
      toast.error('Failed to update contact info');
    } finally {
      setSavingContact(false);
    }
  };

  const getStipStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircle size={16} className="text-emerald-600" />;
      case 'uploaded':
        return <Clock size={16} className="text-amber-600" />;
      case 'needs_review':
        return <AlertTriangle size={16} className="text-amber-600" />;
      default:
        return <X size={16} className="text-red-600" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getAgingClass = (days) => {
    if (days >= thresholds.critical) return 'heat-red';
    if (days >= thresholds.warning) return 'heat-yellow';
    return 'heat-green';
  };

  if (loading || (!isNew && !deal)) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 size={32} className="animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  // Calculate stip completion
  const stips = deal?.stipulations || [];
  const completedStips = stips.filter(s => s.status === 'verified' || s.status === 'uploaded').length;
  const stipProgress = stips.length > 0 ? (completedStips / stips.length) * 100 : 0;

  return (
    <Layout>
      <Header
        title={isNew ? 'New Deal' : `Deal #${deal?.deal_number}`}
        subtitle={isNew ? 'Create a new deal' : deal?.customer_name}
      />

      <div className="p-6" data-testid="deal-detail">
        {/* Back Button and Actions */}
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          
          {/* Deal Lifecycle Controls */}
          {!isNew && deal && (
            <div className="flex items-center gap-3">
              {/* Lifecycle State Badge */}
              <div className={`px-3 py-1 text-xs font-bold uppercase tracking-wider ${
                deal.lifecycle_state === 'UNWOUND' ? 'bg-red-100 text-red-700' :
                deal.lifecycle_state === 'ARCHIVED' ? 'bg-slate-200 text-slate-600' :
                deal.lifecycle_state === 'FUNDED' ? 'bg-emerald-100 text-emerald-700' :
                'bg-blue-100 text-blue-700'
              }`} data-testid="deal-lifecycle-badge">
                {deal.lifecycle_state}
              </div>
              
              {/* Funding Toggle - hidden for unwound deals */}
              {!deal.archived_status && !deal.unwound_status && (
                <Button
                  variant={deal.funding_status ? "default" : "outline"}
                  size="sm"
                  onClick={handleToggleFunding}
                  disabled={togglingFunding}
                  className={deal.funding_status ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                  data-testid="toggle-funding-btn"
                >
                  {togglingFunding ? (
                    <Loader2 size={14} className="mr-1 animate-spin" />
                  ) : deal.funding_status ? (
                    <DollarSign size={14} className="mr-1" />
                  ) : (
                    <ToggleLeft size={14} className="mr-1" />
                  )}
                  {deal.funding_status ? 'FUNDED' : 'Mark Funded'}
                </Button>
              )}
              
              {/* Unwind Button - only for ACTIVE deals (not funded, not archived, not unwound) */}
              {!deal.funding_status && !deal.archived_status && !deal.unwound_status && canUnwind && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openUnwindModal}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="unwind-deal-btn"
                >
                  <XCircle size={14} className="mr-1" />
                  Unwind
                </Button>
              )}

              {/* Reverse Unwind - admin only, unwound deals */}
              {deal.unwound_status && canReverseUnwind && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReverseUnwind}
                  disabled={reversingUnwind}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  data-testid="reverse-unwind-btn"
                >
                  {reversingUnwind ? (
                    <Loader2 size={14} className="mr-1 animate-spin" />
                  ) : (
                    <ArchiveRestore size={14} className="mr-1" />
                  )}
                  Restore to Active
                </Button>
              )}
              
              {/* Archive Button (only for funded, non-unwound deals) */}
              {deal.funding_status && !deal.archived_status && !deal.unwound_status && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleArchiveDeal}
                  disabled={archiving}
                  className="text-slate-600"
                  data-testid="archive-deal-btn"
                >
                  {archiving ? (
                    <Loader2 size={14} className="mr-1 animate-spin" />
                  ) : (
                    <Archive size={14} className="mr-1" />
                  )}
                  Archive
                </Button>
              )}
              
              {/* Unarchive Button (only for archived deals) */}
              {deal.archived_status && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUnarchiveDeal}
                  disabled={unarchiving}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  data-testid="unarchive-deal-btn"
                >
                  {unarchiving ? (
                    <Loader2 size={14} className="mr-1 animate-spin" />
                  ) : (
                    <ArchiveRestore size={14} className="mr-1" />
                  )}
                  Unarchive
                </Button>
              )}
              
              {/* Delete Button - Admin/GM Only */}
              {canDelete && (
                <>
                  {deleteConfirm && (
                    <span className="text-sm text-red-600 font-medium">
                      Are you sure?
                    </span>
                  )}
                  <Button
                    variant={deleteConfirm ? "destructive" : "outline"}
                    size="sm"
                    onClick={handleDeleteDeal}
                    disabled={deleting}
                    className={deleteConfirm ? "" : "text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"}
                    data-testid="delete-deal-btn"
                  >
                    {deleting ? (
                      <><Loader2 size={14} className="mr-1 animate-spin" /> Deleting...</>
                    ) : deleteConfirm ? (
                      <><Trash2 size={14} className="mr-1" /> Confirm</>
                    ) : (
                      <Trash2 size={14} />
                    )}
                  </Button>
                  {deleteConfirm && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirm(false)}
                    >
                      Cancel
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Unwound Deal Banner */}
        {!isNew && deal?.unwound_status && (
          <div className="mb-4 p-4 bg-red-50 border-2 border-red-300 flex items-start justify-between" data-testid="unwound-banner">
            <div className="flex items-start gap-3">
              <XCircle size={22} className="text-red-600 mt-0.5" />
              <div>
                <p className="font-bold text-red-800 text-lg uppercase tracking-wider">This deal is UNWOUND</p>
                <div className="mt-1 text-sm text-red-700 space-y-0.5">
                  <p><span className="font-semibold">Unwind Date:</span> {deal.unwound_at ? new Date(deal.unwound_at).toLocaleString() : 'Unknown'}</p>
                  <p><span className="font-semibold">Unwound By:</span> {deal.unwound_by_name || 'Unknown'}</p>
                  <p>
                    <span className="font-semibold">Reason:</span>{' '}
                    {deal.unwind_reason === 'Other' && deal.unwind_reason_other
                      ? `Other: ${deal.unwind_reason_other}`
                      : (deal.unwind_reason || '—')}
                  </p>
                </div>
                <p className="mt-2 text-xs text-red-600 italic">
                  This deal is preserved for historical tracking. It is excluded from active and funded metrics.
                </p>
              </div>
            </div>
            {canReverseUnwind && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReverseUnwind}
                disabled={reversingUnwind}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                data-testid="unwound-banner-restore-btn"
              >
                {reversingUnwind ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ArchiveRestore size={14} className="mr-1" />}
                Restore to Active
              </Button>
            )}
          </div>
        )}

        {/* Archived Deal Banner */}
        {!isNew && deal?.archived_status && (
          <div className="mb-4 p-4 bg-slate-100 border border-slate-300 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Archive size={20} className="text-slate-500" />
              <div>
                <p className="font-semibold text-slate-700">This deal is archived</p>
                <p className="text-sm text-slate-500">
                  Archived on {deal.archived_at ? new Date(deal.archived_at).toLocaleDateString() : 'Unknown'}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleUnarchiveDeal}
              disabled={unarchiving}
            >
              {unarchiving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ArchiveRestore size={14} className="mr-1" />}
              Restore to Active
            </Button>
          </div>
        )}

        {isNew ? (
          /* New Deal Form */
          <div className="bg-white border border-slate-200 p-6 space-y-6">
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-900">
              Deal Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Customer First Name *
                </Label>
                <Input
                  value={formData.customer_first_name}
                  onChange={(e) => setFormData({ ...formData, customer_first_name: e.target.value })}
                  placeholder="John"
                  className="mt-1"
                  data-testid="input-customer-first-name"
                />
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Customer Last Name *
                </Label>
                <Input
                  value={formData.customer_last_name}
                  onChange={(e) => setFormData({ ...formData, customer_last_name: e.target.value })}
                  placeholder="Doe"
                  className="mt-1"
                  data-testid="input-customer-last-name"
                />
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Deal Number *
                </Label>
                <Input
                  value={formData.deal_number}
                  onChange={(e) => setFormData({ ...formData, deal_number: e.target.value.slice(0, 50) })}
                  placeholder="Enter Deal Number"
                  className="mt-1"
                  data-testid="input-deal-number"
                />
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Deal Date *
                </Label>
                <Input
                  type="date"
                  value={formData.deal_date ? formData.deal_date.split('T')[0] : ''}
                  onChange={(e) => setFormData({ ...formData, deal_date: e.target.value })}
                  className="mt-1"
                  data-testid="input-deal-date"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Vehicle Info *
                </Label>
                <Input
                  value={formData.vehicle_info}
                  onChange={(e) => setFormData({ ...formData, vehicle_info: e.target.value })}
                  placeholder="2024 Honda Accord"
                  className="mt-1"
                  data-testid="input-vehicle-info"
                />
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Stock Number
                </Label>
                <Input
                  value={formData.stock_number}
                  onChange={(e) => setFormData({ ...formData, stock_number: e.target.value })}
                  placeholder="STK12345"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Lender *
                </Label>
                <select
                  value={formData.lender}
                  onChange={(e) => setFormData({ ...formData, lender: e.target.value })}
                  className="w-full h-9 px-3 mt-1 border border-slate-300 text-sm bg-white"
                  data-testid="input-lender"
                >
                  <option value="">Select Lender</option>
                  {lendersList.map(l => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Amount Financed
                </Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">$</span>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={formData.amount_financed ? Number(formData.amount_financed).toLocaleString('en-US') : ''}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9.]/g, '');
                      setFormData({ ...formData, amount_financed: parseFloat(raw) || 0 });
                    }}
                    placeholder="25,000"
                    className="pl-7 font-mono"
                    data-testid="amount-financed-input"
                  />
                </div>
              </div>
              
              {user?.role !== 'finance_manager' && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Finance Manager *
                </Label>
                <select
                  value={formData.finance_manager}
                  onChange={(e) => setFormData({ ...formData, finance_manager: e.target.value })}
                  className="w-full h-9 px-3 mt-1 border border-slate-300 text-sm bg-white"
                  data-testid="input-finance-manager"
                >
                  <option value="">Select Finance Manager</option>
                  {usersList.filter(u => ['admin', 'finance_manager'].includes(u.role)).map(u => (
                    <option key={u.id} value={u.name}>{u.name}</option>
                  ))}
                </select>
              </div>
              )}
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Sales Person
                </Label>
                <select
                  value={formData.sales_person}
                  onChange={(e) => setFormData({ ...formData, sales_person: e.target.value })}
                  className="w-full h-9 px-3 mt-1 border border-slate-300 text-sm bg-white"
                  data-testid="input-sales-person"
                >
                  <option value="">Select Sales Person</option>
                  {usersList.filter(u => u.role === 'staff').map(u => (
                    <option key={u.id} value={u.name}>{u.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Desk Manager
                </Label>
                <select
                  value={formData.desk_manager}
                  onChange={(e) => setFormData({ ...formData, desk_manager: e.target.value })}
                  className="w-full h-9 px-3 mt-1 border border-slate-300 text-sm bg-white"
                  data-testid="new-deal-desk-manager"
                >
                  <option value="">Select Desk Manager</option>
                  {usersList.filter(u => u.role === 'desk_manager').map(u => (
                    <option key={u.id} value={u.name}>{u.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Closer *
                </Label>
                <select
                  value={formData.closer}
                  onChange={(e) => setFormData({ ...formData, closer: e.target.value })}
                  className="w-full h-9 px-3 mt-1 border border-slate-300 text-sm bg-white"
                  data-testid="new-deal-closer"
                >
                  <option value="">Select Closer</option>
                  {usersList.filter(u => u.role === 'closer').map(u => (
                    <option key={u.id} value={u.name}>{u.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Customer Phone
                </Label>
                <Input
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="mt-1"
                  data-testid="input-customer-phone"
                />
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Customer Email
                </Label>
                <Input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  placeholder="customer@email.com"
                  className="mt-1"
                  data-testid="input-customer-email"
                />
              </div>
              
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Comments
                </Label>
                <Input
                  value={formData.comments}
                  onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                  placeholder="Additional notes..."
                  className="mt-1"
                />
              </div>
            </div>

            {/* Stipulations Section */}
            <div className={`border-2 rounded ${stipNoneSelected ? 'border-slate-300 bg-slate-50/60' : 'border-amber-300 bg-amber-50/60'}`} data-testid="new-deal-stips-section">
              <button
                type="button"
                onClick={() => setStipsExpanded(!stipsExpanded)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                data-testid="stips-section-toggle"
              >
                <div className="flex items-center gap-2">
                  <span className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-700">
                    Stipulations *
                  </span>
                  {stipNoneSelected ? (
                    <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded-full font-mono" data-testid="stips-none-badge">
                      None
                    </span>
                  ) : (formSelectedStips.size + formCustomStips.length) > 0 ? (
                    <span className="bg-slate-900 text-white text-xs px-2 py-0.5 rounded-full font-mono" data-testid="stips-count-badge">
                      {formSelectedStips.size + formCustomStips.length}
                    </span>
                  ) : (
                    <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-semibold" data-testid="stips-required-badge">
                      Required
                    </span>
                  )}
                </div>
                <svg
                  className={`w-4 h-4 text-slate-400 transition-transform ${stipsExpanded ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {stipsExpanded && (
                <div className="px-4 pb-4 space-y-4 border-t border-slate-100">
                  {/* None Required checkbox */}
                  <div className="pt-3">
                    <label
                      className={`flex items-center gap-2 px-3 py-2.5 rounded cursor-pointer text-sm border transition-colors ${
                        stipNoneSelected ? 'bg-green-50 border-green-300 text-green-800 font-medium' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                      data-testid="stip-none-required"
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-green-700"
                        checked={stipNoneSelected}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setStipNoneSelected(checked);
                          if (checked) {
                            // Clear all stip selections when "None" is chosen
                            setFormSelectedStips(new Set());
                            setFormCustomStips([]);
                          }
                        }}
                      />
                      No Stipulations Required
                    </label>
                  </div>

                  {!stipNoneSelected && (
                    <>
                      {/* Select All / Deselect All */}
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setFormSelectedStips(new Set(predefinedStips))}
                          className="text-xs font-semibold text-slate-600 hover:text-slate-900 uppercase tracking-wider"
                          data-testid="stips-select-all"
                        >
                          Select All
                        </button>
                        <span className="text-slate-300">|</span>
                        <button
                          type="button"
                          onClick={() => { setFormSelectedStips(new Set()); setFormCustomStips([]); }}
                          className="text-xs font-semibold text-slate-600 hover:text-slate-900 uppercase tracking-wider"
                          data-testid="stips-deselect-all"
                        >
                          Deselect All
                        </button>
                      </div>

                      {/* Predefined Stipulations */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {predefinedStips.map((stip) => {
                          const checked = formSelectedStips.has(stip);
                          return (
                            <label
                              key={stip}
                              className={`flex items-center gap-2 px-3 py-2 rounded cursor-pointer text-sm transition-colors ${
                                checked ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50'
                              }`}
                              data-testid={`stip-option-${stip.toLowerCase().replace(/[\s/']+/g, '-')}`}
                            >
                              <input
                                type="checkbox"
                                className="w-4 h-4 accent-slate-900"
                                checked={checked}
                                onChange={() => {
                                  const next = new Set(formSelectedStips);
                                  if (checked) next.delete(stip); else next.add(stip);
                                  setFormSelectedStips(next);
                                }}
                              />
                              {stip}
                            </label>
                          );
                        })}
                      </div>

                      {/* Custom Stipulations */}
                      {formCustomStips.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Custom</p>
                          <div className="flex flex-wrap gap-2">
                            {formCustomStips.map((stip) => (
                              <span
                                key={stip}
                                className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 border border-amber-200 text-xs px-2.5 py-1 rounded-full"
                                data-testid={`custom-stip-tag-${stip.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                {stip}
                                <button
                                  type="button"
                                  onClick={() => setFormCustomStips(formCustomStips.filter(s => s !== stip))}
                                  className="hover:text-red-600 ml-0.5"
                                  data-testid={`remove-custom-stip-${stip.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  <X size={12} />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Add Custom Stipulation */}
                      <div className="flex gap-2">
                        <Input
                          value={customStipInput}
                          onChange={(e) => setCustomStipInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const val = customStipInput.trim();
                              if (!val) return;
                              if (predefinedStips.includes(val) || formCustomStips.includes(val) || formSelectedStips.has(val)) {
                                toast.error('Stipulation already exists');
                                return;
                              }
                              setFormCustomStips([...formCustomStips, val]);
                              setCustomStipInput('');
                            }
                          }}
                          placeholder="Add custom stipulation..."
                          className="flex-1 text-sm"
                          data-testid="custom-stip-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const val = customStipInput.trim();
                            if (!val) return;
                            if (predefinedStips.includes(val) || formCustomStips.includes(val) || formSelectedStips.has(val)) {
                              toast.error('Stipulation already exists');
                              return;
                            }
                            setFormCustomStips([...formCustomStips, val]);
                            setCustomStipInput('');
                          }}
                          data-testid="add-custom-stip-btn"
                        >
                          <Plus size={14} className="mr-1" /> Add
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Interview Required - Explicit Yes/No */}
            <div className="border border-slate-200 rounded p-4 space-y-3" data-testid="interview-required-section">
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Interview Required *
              </Label>
              <div className="flex gap-4">
                <label
                  className={`flex items-center gap-2 px-4 py-2.5 rounded cursor-pointer text-sm border transition-colors ${
                    interviewChoice === 'yes' ? 'bg-slate-900 text-white border-slate-900 font-medium' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid="interview-yes"
                >
                  <input
                    type="radio"
                    name="interview_required"
                    value="yes"
                    checked={interviewChoice === 'yes'}
                    onChange={() => setInterviewChoice('yes')}
                    className="sr-only"
                  />
                  Yes
                </label>
                <label
                  className={`flex items-center gap-2 px-4 py-2.5 rounded cursor-pointer text-sm border transition-colors ${
                    interviewChoice === 'no' ? 'bg-slate-900 text-white border-slate-900 font-medium' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid="interview-no"
                >
                  <input
                    type="radio"
                    name="interview_required"
                    value="no"
                    checked={interviewChoice === 'no'}
                    onChange={() => setInterviewChoice('no')}
                    className="sr-only"
                  />
                  No
                </label>
              </div>
              {interviewChoice === null && (
                <p className="text-xs text-amber-600 font-medium">You must select Yes or No</p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={() => navigate('/')}>
                Cancel
              </Button>
              <Button onClick={handleSaveDeal} data-testid="save-deal-btn">
                Create Deal
              </Button>
            </div>
          </div>
        ) : (
          /* Existing Deal View */
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList className="bg-slate-100 p-1">
              <TabsTrigger value="overview" className="data-[state=active]:bg-white">
                <FileText size={16} className="mr-2" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="stips" className="data-[state=active]:bg-white">
                <Clock size={16} className="mr-2" />
                Stipulations
              </TabsTrigger>
              <TabsTrigger value="interview" className="data-[state=active]:bg-white">
                <Phone size={16} className="mr-2" />
                Interview
              </TabsTrigger>
              <TabsTrigger value="compliance" className="data-[state=active]:bg-white">
                <Shield size={16} className="mr-2" />
                Compliance
              </TabsTrigger>
              <TabsTrigger value="activity" className="data-[state=active]:bg-white">
                <Activity size={16} className="mr-2" />
                Activity
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Deal Overview Card */}
                <div className="lg:col-span-2 bg-white border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900">
                      Deal Overview
                    </h3>
                    {!editingOverview && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs"
                        data-testid="edit-overview-btn"
                        onClick={() => {
                          setEditingOverview(true);
                          setOverviewForm({
                            deal_number: deal.deal_number || '',
                            customer_name: deal.customer_name || '',
                            vehicle_info: deal.vehicle_info || '',
                            lender: deal.lender || '',
                            finance_manager: deal.finance_manager || '',
                            sales_person: deal.sales_person || '',
                            desk_manager: deal.desk_manager || '',
                            amount_financed: deal.amount_financed || 0,
                          });
                        }}
                      >
                        <Edit3 size={14} className="mr-1" /> Edit
                      </Button>
                    )}
                  </div>

                  {editingOverview ? (
                    <div className="space-y-4" data-testid="overview-edit-form">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-slate-500">Deal Number</Label>
                          <Input
                            value={overviewForm.deal_number}
                            onChange={(e) => setOverviewForm({ ...overviewForm, deal_number: e.target.value.slice(0, 50) })}
                            className="mt-1"
                            data-testid="edit-overview-deal-number"
                          />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-slate-500">Customer</Label>
                          <Input
                            value={overviewForm.customer_name}
                            onChange={(e) => setOverviewForm({ ...overviewForm, customer_name: e.target.value })}
                            className="mt-1"
                            data-testid="edit-overview-customer-name"
                          />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-slate-500">Vehicle</Label>
                          <Input
                            value={overviewForm.vehicle_info}
                            onChange={(e) => setOverviewForm({ ...overviewForm, vehicle_info: e.target.value })}
                            className="mt-1"
                            data-testid="edit-overview-vehicle"
                          />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-slate-500">Lender</Label>
                          <select
                            value={overviewForm.lender}
                            onChange={(e) => setOverviewForm({ ...overviewForm, lender: e.target.value })}
                            className="w-full h-9 px-3 mt-1 border border-slate-300 text-sm bg-white"
                            data-testid="edit-overview-lender"
                          >
                            <option value="">Select Lender</option>
                            {lendersList.map(l => (
                              <option key={l} value={l}>{l}</option>
                            ))}
                            {overviewForm.lender && !lendersList.includes(overviewForm.lender) && (
                              <option value={overviewForm.lender}>{overviewForm.lender}</option>
                            )}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-slate-500">Finance Manager</Label>
                          <select
                            value={overviewForm.finance_manager}
                            onChange={(e) => setOverviewForm({ ...overviewForm, finance_manager: e.target.value })}
                            className="w-full h-9 px-3 mt-1 border border-slate-300 text-sm bg-white"
                            data-testid="edit-overview-finance-manager"
                          >
                            <option value="">Select Finance Manager</option>
                            {usersList.filter(u => ['admin', 'finance_manager'].includes(u.role)).map(u => (
                              <option key={u.id} value={u.name}>{u.name}</option>
                            ))}
                            {overviewForm.finance_manager && !usersList.some(u => ['admin', 'finance_manager'].includes(u.role) && u.name === overviewForm.finance_manager) && (
                              <option value={overviewForm.finance_manager}>{overviewForm.finance_manager}</option>
                            )}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-slate-500">Sales Person</Label>
                          <select
                            value={overviewForm.sales_person}
                            onChange={(e) => setOverviewForm({ ...overviewForm, sales_person: e.target.value })}
                            className="w-full h-9 px-3 mt-1 border border-slate-300 text-sm bg-white"
                            data-testid="edit-overview-sales-person"
                          >
                            <option value="">Select Sales Person</option>
                            {usersList.filter(u => u.role === 'staff').map(u => (
                              <option key={u.id} value={u.name}>{u.name}</option>
                            ))}
                            {overviewForm.sales_person && !usersList.some(u => u.role === 'staff' && u.name === overviewForm.sales_person) && (
                              <option value={overviewForm.sales_person}>{overviewForm.sales_person}</option>
                            )}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-slate-500">Desk Manager</Label>
                          <select
                            value={overviewForm.desk_manager}
                            onChange={(e) => setOverviewForm({ ...overviewForm, desk_manager: e.target.value })}
                            className="w-full h-9 px-3 mt-1 border border-slate-300 text-sm bg-white"
                            data-testid="edit-overview-desk-manager"
                          >
                            <option value="">Select Desk Manager</option>
                            {usersList.filter(u => u.role === 'desk_manager').map(u => (
                              <option key={u.id} value={u.name}>{u.name}</option>
                            ))}
                            {overviewForm.desk_manager && !usersList.some(u => u.role === 'desk_manager' && u.name === overviewForm.desk_manager) && (
                              <option value={overviewForm.desk_manager}>{overviewForm.desk_manager}</option>
                            )}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-slate-500">Amount Financed</Label>
                          <Input
                            value={overviewForm.amount_financed ? Number(overviewForm.amount_financed).toLocaleString('en-US') : ''}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/[^0-9.]/g, '');
                              setOverviewForm({ ...overviewForm, amount_financed: parseFloat(raw) || 0 });
                            }}
                            className="mt-1"
                            data-testid="edit-overview-amount"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pt-2">
                        <Button
                          size="sm"
                          disabled={savingOverview}
                          data-testid="save-overview-btn"
                          onClick={async () => {
                            const dn = overviewForm.deal_number.trim();
                            if (!dn) { toast.error('Deal Number is required'); return; }
                            if (!overviewForm.customer_name.trim()) { toast.error('Customer Name is required'); return; }
                            setSavingOverview(true);
                            try {
                              await updateDeal(id, {
                                deal_number: dn,
                                customer_name: overviewForm.customer_name.trim(),
                                vehicle_info: overviewForm.vehicle_info.trim(),
                                lender: overviewForm.lender.trim(),
                                finance_manager: overviewForm.finance_manager.trim(),
                                sales_person: overviewForm.sales_person.trim(),
                                amount_financed: overviewForm.amount_financed,
                                pending_funds: overviewForm.amount_financed,
                              });
                              toast.success('Deal overview updated');
                              setEditingOverview(false);
                              loadDeal();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to update deal');
                            } finally {
                              setSavingOverview(false);
                            }
                          }}
                        >
                          {savingOverview ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
                          Save Changes
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingOverview(false)} data-testid="cancel-overview-btn">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Deal Number</p>
                        <p className="text-lg font-mono font-bold text-slate-900" data-testid="deal-number-display">{deal.deal_number || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Customer</p>
                        <p className="text-lg font-medium text-slate-900" data-testid="customer-name-display">{deal.customer_name}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Vehicle</p>
                        <p className="text-lg font-medium text-slate-900" data-testid="vehicle-display">{deal.vehicle_info}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Lender</p>
                        <p className="text-lg font-medium text-slate-900" data-testid="lender-display">{deal.lender}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Finance Manager</p>
                        <p className="text-lg font-medium text-slate-900" data-testid="finance-manager-display">{deal.finance_manager}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Sales Person</p>
                        <p className="text-lg font-medium text-slate-900" data-testid="sales-person-display">{deal.sales_person || 'Not assigned'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Desk Manager</p>
                        <p className="text-lg font-medium text-slate-900" data-testid="desk-manager-display">{deal.desk_manager || 'Not assigned'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Amount Financed</p>
                        <p className="text-lg font-mono font-bold text-slate-900" data-testid="amount-display">{formatCurrency(deal.amount_financed)}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Cash at Risk</p>
                        <p className="text-lg font-mono font-bold text-red-600" data-testid="cash-at-risk-display">{formatCurrency(deal.pending_funds || deal.amount_financed)}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Customer Contact Info */}
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Customer Contact</h4>
                      {!editingContact && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleEditContact}
                          className="text-xs"
                          data-testid="edit-contact-btn"
                        >
                          Edit
                        </Button>
                      )}
                    </div>
                    
                    {editingContact ? (
                      <div className="space-y-3 p-4 bg-slate-50 border border-slate-200">
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-slate-500">Phone</Label>
                          <Input
                            value={contactForm.phone}
                            onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                            placeholder="(555) 123-4567"
                            className="mt-1"
                            data-testid="edit-customer-phone"
                          />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
                          <Input
                            type="email"
                            value={contactForm.email}
                            onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                            placeholder="customer@email.com"
                            className="mt-1"
                            data-testid="edit-customer-email"
                          />
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            onClick={handleSaveContact}
                            disabled={savingContact}
                            data-testid="save-contact-btn"
                          >
                            {savingContact ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
                            Save
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingContact(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 flex items-center justify-center ${deal.customer_phone ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                            <Phone size={16} />
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Phone</p>
                            <p className={`font-medium ${deal.customer_phone ? 'text-slate-900' : 'text-slate-400 italic'}`}>
                              {deal.customer_phone || 'Not provided'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 flex items-center justify-center ${deal.customer_email ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                            <Mail size={16} />
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Email</p>
                            <p className={`font-medium ${deal.customer_email ? 'text-slate-900' : 'text-slate-400 italic'}`}>
                              {deal.customer_email || 'Not provided'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Deal Age Card */}
                <div className="bg-white border border-slate-200 p-6">
                  <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900 mb-4">
                    Deal Age
                  </h3>
                  <div className={`inline-flex items-center justify-center w-20 h-20 text-3xl font-mono font-bold ${getAgingClass(deal.deal_age)}`}>
                    {deal.deal_age}
                  </div>
                  <p className="text-sm text-slate-500 mt-2">days in transit</p>
                  
                  {deal.deal_age >= thresholds.critical && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200">
                      <p className="text-sm text-red-700 font-medium flex items-center gap-2">
                        <AlertTriangle size={16} />
                        Critical: Escalate to GM
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Deal Notes Section - Full Width */}
              <div className="bg-white border border-slate-200 p-6" data-testid="deal-notes-overview">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <StickyNote size={20} className="text-slate-600" />
                    <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900">
                      Deal Notes
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">
                      {dealNotes.filter(n => n.note_type === 'internal' || !n.note_type).length} internal, {dealNotes.filter(n => n.note_type === 'lender_facing').length} lender-facing, {dealNotes.filter(n => n.note_type === 'admin_alert').length} admin alerts
                    </span>
                  </div>
                </div>

                {/* Note Type Tabs */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setNoteFilter('all')}
                    className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                      noteFilter === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid="filter-all-notes"
                  >
                    All Notes
                  </button>
                  <button
                    onClick={() => setNoteFilter('internal')}
                    className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                      noteFilter === 'internal' ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                    }`}
                    data-testid="filter-internal-notes"
                  >
                    Internal
                  </button>
                  <button
                    onClick={() => setNoteFilter('lender_facing')}
                    className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                      noteFilter === 'lender_facing' ? 'bg-blue-500 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                    }`}
                    data-testid="filter-lender-notes"
                  >
                    Lender-Facing
                  </button>
                  <button
                    onClick={() => setNoteFilter('admin_alert')}
                    className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                      noteFilter === 'admin_alert' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100'
                    }`}
                    data-testid="filter-admin-alert-notes"
                  >
                    Admin Alerts
                  </button>
                </div>

                {/* Add New Note */}
                <div className="mb-4 p-4 bg-slate-50 border border-slate-200">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Add Note</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setNewNoteType('internal')}
                        className={`px-2 py-1 text-xs font-semibold transition-colors ${
                          newNoteType === 'internal' 
                            ? 'bg-amber-500 text-white' 
                            : 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
                        }`}
                        data-testid="new-note-type-internal"
                      >
                        Internal
                      </button>
                      <button
                        onClick={() => setNewNoteType('lender_facing')}
                        className={`px-2 py-1 text-xs font-semibold transition-colors ${
                          newNoteType === 'lender_facing' 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                        }`}
                        data-testid="new-note-type-lender"
                      >
                        Lender-Facing
                      </button>
                      {user?.role === 'admin' && (
                        <button
                          onClick={() => setNewNoteType('admin_alert')}
                          className={`px-2 py-1 text-xs font-semibold transition-colors ${
                            newNoteType === 'admin_alert' 
                              ? 'bg-red-600 text-white' 
                              : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                          }`}
                          data-testid="new-note-type-admin-alert"
                        >
                          Admin Alert
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {newNoteType === 'internal' && (
                    <div className="mb-2 p-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-center gap-2">
                      <Shield size={14} />
                      Internal notes are private to your dealership. They feed AI Interview Prep (safely transformed).
                    </div>
                  )}
                  {newNoteType === 'lender_facing' && (
                    <div className="mb-2 p-2 bg-blue-50 border border-blue-200 text-blue-800 text-xs flex items-center gap-2">
                      <Eye size={14} />
                      Lender-facing notes may be referenced in external workflows. Keep content professional.
                    </div>
                  )}
                  {newNoteType === 'admin_alert' && (
                    <div className="mb-2 p-2 bg-red-50 border border-red-300 text-red-800 text-xs flex items-center gap-2">
                      <AlertTriangle size={14} />
                      Admin Alert: This note will be highlighted in red, auto-pinned, and all users will be notified immediately.
                    </div>
                  )}
                  
                  <textarea
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    placeholder={newNoteType === 'internal' 
                      ? "Enter internal notes, deal context, manager instructions, or follow-up reminders..."
                      : "Enter lender-appropriate notes, deal status updates, or documentation notes..."
                    }
                    className="w-full h-20 p-3 border border-slate-300 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-slate-500"
                    data-testid="new-note-input"
                  />
                  <div className="flex justify-end mt-2">
                    <Button
                      onClick={handleSaveNote}
                      disabled={savingNote || !newNoteText.trim()}
                      size="sm"
                      data-testid="save-note-btn"
                    >
                      {savingNote ? (
                        <><Loader2 size={14} className="mr-1 animate-spin" /> Saving...</>
                      ) : (
                        <><Save size={14} className="mr-1" /> Save Note</>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Notes List */}
                {filteredNotes.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {filteredNotes.map((note) => (
                      <div 
                        key={note.id} 
                        className={`p-4 border ${
                          note.note_type === 'admin_alert'
                            ? 'bg-red-50 border-red-300 ring-1 ring-red-200'
                            : note.is_pinned
                              ? 'bg-yellow-50 border-yellow-300 ring-1 ring-yellow-200'
                              : note.note_type === 'lender_facing' 
                                ? 'bg-blue-50 border-blue-200' 
                                : 'bg-amber-50 border-amber-200'
                        }`}
                        data-testid={`note-${note.id}`}
                      >
                        {editingNoteId === note.id ? (
                          <div>
                            <textarea
                              value={editNoteText}
                              onChange={(e) => setEditNoteText(e.target.value)}
                              className="w-full h-20 p-3 border border-slate-300 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-slate-500"
                            />
                            <div className="flex justify-end gap-2 mt-2">
                              <Button
                                onClick={() => { setEditingNoteId(null); setEditNoteText(''); }}
                                variant="outline"
                                size="sm"
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={() => handleUpdateNote(note.id)}
                                disabled={savingNote}
                                size="sm"
                              >
                                {savingNote ? 'Saving...' : 'Update'}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-semibold uppercase tracking-wider px-2 py-0.5 ${
                                  note.note_type === 'admin_alert'
                                    ? 'bg-red-200 text-red-800'
                                    : note.note_type === 'lender_facing' 
                                      ? 'bg-blue-200 text-blue-800' 
                                      : 'bg-amber-200 text-amber-800'
                                }`}>
                                  {note.note_type === 'admin_alert' ? 'Admin Alert' : note.note_type === 'lender_facing' ? 'Lender-Facing' : 'Internal'}
                                </span>
                                {note.is_pinned && (
                                  <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 bg-yellow-200 text-yellow-800">
                                    Pinned
                                  </span>
                                )}
                              </div>
                              {(note.created_by_user_id === user?.id || user?.role === 'admin') && (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handlePinNote(note.id)}
                                    className={`${note.is_pinned ? 'text-yellow-600' : 'text-slate-400 hover:text-yellow-600'}`}
                                    title={note.is_pinned ? 'Unpin' : 'Pin'}
                                    data-testid={`pin-note-${note.id}`}
                                  >
                                    <Pin size={14} className={note.is_pinned ? 'fill-current' : ''} />
                                  </button>
                                  <button
                                    onClick={() => { setEditingNoteId(note.id); setEditNoteText(note.note_text); }}
                                    className="text-slate-500 hover:text-slate-700"
                                  >
                                    <Edit3 size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteNote(note.id)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              )}
                            </div>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap mb-2">{note.note_text}</p>
                            <p className="text-xs text-slate-500">
                              By {note.created_by_name} ({note.created_by_role}) • {new Date(note.created_at).toLocaleString()}
                            </p>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <StickyNote size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No {noteFilter === 'all' ? '' : noteFilter.replace('_', '-') + ' '}notes yet</p>
                    <p className="text-xs mt-1">Add notes to document deal context</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Stipulations Tab */}
            <TabsContent value="stips" className="space-y-4">
              <div className="bg-white border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900">
                    Required Documents
                  </h3>
                  <span className="text-sm text-slate-500">
                    {completedStips} of {stips.length} complete
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <Progress value={stipProgress} className="h-3" />
                  <p className="text-xs text-slate-500 mt-1">{Math.round(stipProgress)}% complete</p>
                </div>

                {/* Add Custom Stipulation */}
                <div className="mb-4 p-4 bg-slate-50 border border-slate-200">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Add Stipulation</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newStipName}
                      onChange={(e) => setNewStipName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddStip()}
                      placeholder="Enter custom stipulation name..."
                      className="flex-1 px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                      data-testid="add-stip-input"
                    />
                    <Button
                      onClick={handleAddStip}
                      disabled={addingStip || !newStipName.trim()}
                      size="sm"
                      data-testid="add-stip-btn"
                    >
                      {addingStip ? (
                        <Loader2 size={14} className="mr-1 animate-spin" />
                      ) : (
                        <Plus size={14} className="mr-1" />
                      )}
                      Add
                    </Button>
                  </div>
                </div>

                {/* Select All / Deselect All Controls */}
                <div className="flex items-center justify-between mb-4 p-3 bg-slate-100 border border-slate-200">
                  <div className="flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSelectAllStips}
                      className="text-xs"
                      data-testid="select-all-stips"
                    >
                      <CheckSquare size={14} className="mr-1" />
                      Select All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDeselectAllStips}
                      className="text-xs"
                      data-testid="deselect-all-stips"
                    >
                      <Square size={14} className="mr-1" />
                      Deselect All
                    </Button>
                    {selectedStips.length > 0 && (
                      <span className="text-xs text-slate-500">
                        {selectedStips.length} selected
                      </span>
                    )}
                  </div>
                  
                  {/* Bulk Actions */}
                  {selectedStips.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={handleBulkVerify}
                        className="text-xs"
                        data-testid="bulk-verify-btn"
                      >
                        <Check size={14} className="mr-1" />
                        Verify Selected
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleBulkReset}
                        className="text-xs"
                        data-testid="bulk-reset-btn"
                      >
                        <RefreshCw size={14} className="mr-1" />
                        Reset to Missing
                      </Button>
                    </div>
                  )}
                </div>

                {/* Stips List */}
                <div className="space-y-3">
                  {stips.map((stip, idx) => (
                    <div
                      key={`${stip.name}-${idx}`}
                      className={`flex items-center justify-between p-4 border transition-all ${
                        selectedStips.includes(stip.name) ? 'ring-2 ring-blue-500 ring-offset-1' : ''
                      } ${
                        stip.status === 'missing' ? 'border-red-200 bg-red-50' :
                        stip.status === 'verified' ? 'border-emerald-200 bg-emerald-50' :
                        'border-amber-200 bg-amber-50'
                      }`}
                      data-testid={`stip-${stip.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="flex items-center gap-3">
                        {/* Selection Checkbox */}
                        <button
                          onClick={() => handleToggleStipSelection(stip.name)}
                          className={`w-5 h-5 border-2 flex items-center justify-center transition-colors ${
                            selectedStips.includes(stip.name) 
                              ? 'bg-blue-500 border-blue-500 text-white' 
                              : 'border-slate-300 hover:border-slate-400'
                          }`}
                          data-testid={`select-stip-${idx}`}
                        >
                          {selectedStips.includes(stip.name) && <Check size={12} />}
                        </button>
                        
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{stip.name}</p>
                            {stip.is_custom && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5">Custom</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-slate-500 capitalize">{stip.status.replace('_', ' ')}</p>
                            {stip.original_filename && (
                              <span className="text-xs text-slate-400">• {stip.original_filename}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {stip.status === 'missing' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStipUpload(stip.name)}
                            data-testid={`upload-stip-${idx}`}
                          >
                            <Upload size={14} className="mr-1" />
                            Upload
                          </Button>
                        )}
                        {(stip.status === 'uploaded' || stip.status === 'verified') && stip.file_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewFile(stip)}
                            data-testid={`view-stip-${idx}`}
                          >
                            <Eye size={14} className="mr-1" />
                            View
                          </Button>
                        )}
                        {(stip.status === 'uploaded' || stip.status === 'verified') && stip.file_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadFile(stip)}
                            data-testid={`download-stip-${idx}`}
                          >
                            <FileText size={14} className="mr-1" />
                            Download
                          </Button>
                        )}
                        {stip.status === 'uploaded' && (
                          <Button
                            size="sm"
                            onClick={() => handleStipVerify(stip.name)}
                            data-testid={`verify-stip-${idx}`}
                          >
                            <Check size={14} className="mr-1" />
                            Verify
                          </Button>
                        )}
                        
                        {/* Remove Button */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemoveStip(stip.name)}
                          disabled={removingStip === stip.name}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`remove-stip-${idx}`}
                        >
                          {removingStip === stip.name ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <X size={14} />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Empty State */}
                {stips.length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <FileText size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No stipulations added</p>
                    <p className="text-xs mt-1">Add required documents above</p>
                  </div>
                )}

                {/* Uploaded Documents Summary */}
                {stips.filter(s => s.file_id).length > 0 && (
                  <div className="mt-6 pt-4 border-t border-slate-200">
                    <h4 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">
                      Uploaded Documents
                    </h4>
                    <div className="bg-slate-50 border border-slate-200 divide-y divide-slate-200">
                      {stips.filter(s => s.file_id).map((stip, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3">
                          <div className="flex items-center gap-3">
                            <FileText size={18} className="text-slate-400" />
                            <div>
                              <p className="text-sm font-medium text-slate-900">{stip.name}</p>
                              <p className="text-xs text-slate-500">{stip.original_filename}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleViewFile(stip)}
                              className="text-xs"
                            >
                              <Eye size={12} className="mr-1" />
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDownloadFile(stip)}
                              className="text-xs"
                            >
                              <Download size={12} className="mr-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Interview Tab */}
            <TabsContent value="interview" className="space-y-4">
              {/* Overdue Warning Banner */}
              {deal.interview_is_overdue && (
                <div className="bg-red-50 border-2 border-red-300 p-4 flex items-center gap-3" data-testid="interview-overdue-banner">
                  <AlertTriangle className="text-red-600 flex-shrink-0" size={24} />
                  <div>
                    <p className="font-bold text-red-800">Interview Overdue - Immediate Action Required</p>
                    <p className="text-sm text-red-600">{deal.interview_overdue_reason}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Interview Status */}
                <div className={`bg-white border p-6 ${deal.interview_is_overdue ? 'border-red-300' : 'border-slate-200'}`}>
                  <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900 mb-4">
                    Interview Status
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Required</p>
                      <Select
                        value={deal.interview_required ? 'yes' : 'no'}
                        onValueChange={(v) => handleStatusUpdate('interview_required', v === 'yes')}
                      >
                        <SelectTrigger className="w-32" data-testid="interview-required-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Status</p>
                      <Select
                        value={deal.interview_status || 'not_started'}
                        onValueChange={(v) => handleStatusUpdate('interview_status', v)}
                      >
                        <SelectTrigger className="w-44" data-testid="interview-status-select">
                          <SelectValue>
                            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${
                              deal.interview_is_overdue ? 'bg-red-100 text-red-700' :
                              deal.interview_status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                              deal.interview_status === 'scheduled' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
                            }`}>
                              {deal.interview_is_overdue ? 'OVERDUE' : deal.interview_status?.replace('_', ' ').toUpperCase()}
                            </span>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not_started">Not Started</SelectItem>
                          <SelectItem value="scheduled">Scheduled</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Escalation Level */}
                    {deal.interview_escalation_level > 0 && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Escalation Level</p>
                        <span className={`inline-flex px-3 py-1 text-sm font-bold ${
                          deal.interview_escalation_level >= 2 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          Level {deal.interview_escalation_level}
                          {deal.interview_escalation_level === 1 && ' - Finance Manager'}
                          {deal.interview_escalation_level === 2 && ' - Finance Director'}
                          {deal.interview_escalation_level >= 3 && ' - CRITICAL'}
                        </span>
                      </div>
                    )}
                    
                    {deal.interview_scheduled_time && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Scheduled For</p>
                        <p className="text-lg font-medium text-slate-900 mb-2">
                          {new Date(deal.interview_scheduled_time).toLocaleString()}
                        </p>
                        {/* Reschedule Controls */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" size="sm" data-testid="reschedule-date-picker">
                                <CalendarIcon size={14} className="mr-1" />
                                {rescheduleDate ? format(rescheduleDate, 'PPP') + ` ${String(rescheduleDate.getHours()).padStart(2,'0')}:${String(rescheduleDate.getMinutes()).padStart(2,'0')}` : 'Pick new date & time'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={rescheduleDate}
                                onSelect={(day) => {
                                  if (!day) return;
                                  const prev = rescheduleDate || new Date();
                                  day.setHours(prev.getHours(), prev.getMinutes());
                                  setRescheduleDate(new Date(day));
                                }}
                                initialFocus
                              />
                              <div className="flex items-center gap-2 px-4 pb-3 border-t pt-3">
                                <label className="text-xs font-semibold text-slate-500 uppercase">Time</label>
                                <select
                                  className="border rounded px-2 py-1 text-sm"
                                  value={rescheduleDate ? rescheduleDate.getHours() : 9}
                                  onChange={(e) => {
                                    const d = new Date(rescheduleDate || new Date());
                                    d.setHours(parseInt(e.target.value));
                                    setRescheduleDate(new Date(d));
                                  }}
                                  data-testid="reschedule-hour"
                                >
                                  {Array.from({length: 24}, (_, i) => (
                                    <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                                  ))}
                                </select>
                                <span className="font-bold text-slate-400">:</span>
                                <select
                                  className="border rounded px-2 py-1 text-sm"
                                  value={rescheduleDate ? rescheduleDate.getMinutes() : 0}
                                  onChange={(e) => {
                                    const d = new Date(rescheduleDate || new Date());
                                    d.setMinutes(parseInt(e.target.value));
                                    setRescheduleDate(new Date(d));
                                  }}
                                  data-testid="reschedule-minute"
                                >
                                  {[0, 15, 30, 45].map(m => (
                                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                                  ))}
                                </select>
                              </div>
                            </PopoverContent>
                          </Popover>
                          <Button
                            size="sm"
                            onClick={handleRescheduleInterview}
                            disabled={!rescheduleDate || rescheduling}
                            data-testid="reschedule-interview-btn"
                          >
                            {rescheduling ? (
                              <Loader2 size={14} className="mr-1 animate-spin" />
                            ) : (
                              <RefreshCw size={14} className="mr-1" />
                            )}
                            Reschedule
                          </Button>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">Rescheduling will notify the customer via SMS and email.</p>
                      </div>
                    )}
                    
                    {deal.interview_completed_at && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Completed At</p>
                        <p className="text-lg font-medium text-emerald-600">
                          {new Date(deal.interview_completed_at).toLocaleString()}
                        </p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Reminders Sent</p>
                        <p className={`text-lg font-mono font-bold ${deal.interview_reminders_sent > 2 ? 'text-red-600' : 'text-slate-900'}`}>
                          {deal.interview_reminders_sent}
                        </p>
                      </div>
                      {deal.interview_last_reminder_at && (
                        <div>
                          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Last Reminder</p>
                          <p className="text-sm text-slate-600">
                            {new Date(deal.interview_last_reminder_at).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  {deal.interview_required && deal.interview_status !== 'completed' && (
                    <div className="mt-6 pt-4 border-t border-slate-200 space-y-3">
                      {deal.interview_status === 'not_started' && (
                        <div className="space-y-2">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full">
                                <CalendarIcon size={16} className="mr-2" />
                                {interviewDate ? format(interviewDate, 'PPP') + ` ${String(interviewDate.getHours()).padStart(2,'0')}:${String(interviewDate.getMinutes()).padStart(2,'0')}` : 'Select Date & Time'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={interviewDate}
                                onSelect={(day) => {
                                  if (!day) return;
                                  const prev = interviewDate || new Date();
                                  day.setHours(prev.getHours(), prev.getMinutes());
                                  setInterviewDate(new Date(day));
                                }}
                                initialFocus
                              />
                              <div className="flex items-center gap-2 px-4 pb-3 border-t pt-3">
                                <label className="text-xs font-semibold text-slate-500 uppercase">Time</label>
                                <select
                                  className="border rounded px-2 py-1 text-sm"
                                  value={interviewDate ? interviewDate.getHours() : 9}
                                  onChange={(e) => {
                                    const d = new Date(interviewDate || new Date());
                                    d.setHours(parseInt(e.target.value));
                                    setInterviewDate(new Date(d));
                                  }}
                                  data-testid="schedule-hour"
                                >
                                  {Array.from({length: 24}, (_, i) => (
                                    <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                                  ))}
                                </select>
                                <span className="font-bold text-slate-400">:</span>
                                <select
                                  className="border rounded px-2 py-1 text-sm"
                                  value={interviewDate ? interviewDate.getMinutes() : 0}
                                  onChange={(e) => {
                                    const d = new Date(interviewDate || new Date());
                                    d.setMinutes(parseInt(e.target.value));
                                    setInterviewDate(new Date(d));
                                  }}
                                  data-testid="schedule-minute"
                                >
                                  {[0, 15, 30, 45].map(m => (
                                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                                  ))}
                                </select>
                              </div>
                            </PopoverContent>
                          </Popover>
                          <Button
                            onClick={handleScheduleInterview}
                            className="w-full"
                            disabled={!interviewDate}
                            data-testid="schedule-interview-btn"
                          >
                            Schedule Interview
                          </Button>
                        </div>
                      )}
                      
                      {deal.interview_status === 'scheduled' && (
                        <>
                          <Button
                            onClick={handleSendReminder}
                            variant={deal.interview_is_overdue ? "destructive" : "outline"}
                            className="w-full"
                            data-testid="send-reminder-btn"
                          >
                            <Send size={16} className="mr-2" />
                            Send Reminder
                          </Button>
                          <Button
                            onClick={handleCompleteInterview}
                            className="w-full"
                            data-testid="complete-interview-btn"
                          >
                            <Check size={16} className="mr-2" />
                            Mark as Completed
                          </Button>
                        </>
                      )}
                      
                      {/* Manual Reminder for Not Started deals */}
                      {deal.interview_status === 'not_started' && deal.interview_is_overdue && (
                        <Button
                          onClick={handleSendReminder}
                          variant="destructive"
                          className="w-full"
                          data-testid="send-overdue-reminder-btn"
                        >
                          <AlertTriangle size={16} className="mr-2" />
                          Send Overdue Reminder
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {/* Completed State */}
                  {deal.interview_status === 'completed' && (
                    <div className="mt-6 pt-4 border-t border-slate-200">
                      <div className="flex items-center gap-3 text-emerald-600">
                        <Check size={24} />
                        <span className="font-semibold">Interview Completed</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* AI Message Generator */}
                <div className="bg-white border border-slate-200 p-6">
                  <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900 mb-4">
                    AI Interview Concierge
                  </h3>
                  
                  <div className="space-y-3">
                    <Button
                      onClick={() => handleGenerateMessage('intro')}
                      variant="outline"
                      className="w-full"
                      disabled={messageLoading}
                    >
                      <Mail size={16} className="mr-2" />
                      Generate Intro Message
                    </Button>
                    <Button
                      onClick={() => handleGenerateMessage('confirmation')}
                      variant="outline"
                      className="w-full"
                      disabled={messageLoading}
                    >
                      <CheckCircle size={16} className="mr-2" />
                      Generate Confirmation
                    </Button>
                    
                    {/* AI Interview Prep with outdated warning */}
                    <div className="pt-2 border-t border-slate-200">
                      {aiPrepOutdated && dealNotes.length > 0 && (
                        <div className="mb-2 p-2 bg-amber-50 border border-amber-200 text-amber-700 text-xs flex items-center gap-2">
                          <AlertCircle size={14} />
                          Notes updated. Regenerate AI prep for latest coaching.
                        </div>
                      )}
                      <Button
                        onClick={() => handleAIAnalysis('interview_prep')}
                        className="w-full"
                        disabled={aiLoading}
                        data-testid="ai-interview-prep-btn"
                      >
                        {aiLoading ? (
                          <><Loader2 size={16} className="mr-2 animate-spin" /> Generating...</>
                        ) : (
                          <><Sparkles size={16} className="mr-2" /> AI Interview Prep</>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Customer Message Output */}
                  {generatedMessage && (
                    <div className="mt-4 p-4 bg-slate-50 border border-slate-200">
                      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                        Customer Message
                      </p>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap mb-4">
                        {generatedMessage}
                      </p>
                      
                      <div className="flex flex-col gap-3 pt-3 border-t border-slate-200">
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                          Send to Customer
                        </p>
                        <div className="flex gap-3">
                          <Button
                            onClick={handleSendSMS}
                            disabled={sendingSMS || !deal.customer_phone}
                            variant="outline"
                            className="flex-1"
                            data-testid="send-sms-btn"
                          >
                            {sendingSMS ? (
                              <><Loader2 size={16} className="mr-2 animate-spin" /> Sending...</>
                            ) : (
                              <><MessageSquare size={16} className="mr-2" /> Send via SMS
                                {!deal.customer_phone && <span className="ml-1 text-xs text-amber-600">(No phone)</span>}
                              </>
                            )}
                          </Button>
                          
                          <Button
                            onClick={handleSendEmail}
                            disabled={sendingEmail || !deal.customer_email}
                            variant="outline"
                            className="flex-1"
                            data-testid="send-email-btn"
                          >
                            {sendingEmail ? (
                              <><Loader2 size={16} className="mr-2 animate-spin" /> Sending...</>
                            ) : (
                              <><Mail size={16} className="mr-2" /> Send via Email
                                {!deal.customer_email && <span className="ml-1 text-xs text-amber-600">(No email)</span>}
                              </>
                            )}
                          </Button>
                        </div>
                        
                        {(!deal.customer_phone || !deal.customer_email) && (
                          <p className="text-xs text-amber-600 mt-1">
                            Add customer contact info in the Overview tab to enable sending.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes Reference - Points to Overview */}
              <div className="bg-slate-50 border border-slate-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <StickyNote size={18} className="text-slate-500" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">Deal Notes</p>
                      <p className="text-xs text-slate-500">
                        {dealNotes.filter(n => n.note_type === 'internal' || !n.note_type).length} internal notes available • AI Interview Prep uses internal notes (safely transformed)
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.querySelector('[data-testid="deal-notes-overview"]')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    View in Overview
                  </Button>
                </div>
              </div>

              {/* AI Interview Prep Output - Full Width with Enhanced Structure */}
              {(aiPrepContent || (aiResponse && !generatedMessage)) && (
                <div className="bg-white border-2 border-emerald-200 p-6" data-testid="ai-prep-output">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Sparkles size={20} className="text-emerald-600" />
                      <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900">
                        AI Interview Prep
                      </h3>
                      {dealNotes.filter(n => n.note_type === 'internal' || !n.note_type).length > 0 && (
                        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 font-semibold">
                          USES INTERNAL NOTES
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleAIAnalysis('interview_prep')}
                        variant="outline"
                        size="sm"
                        disabled={aiLoading}
                      >
                        <RefreshCw size={14} className={`mr-1 ${aiLoading ? 'animate-spin' : ''}`} />
                        Regenerate
                      </Button>
                      <Button
                        onClick={handleCopyAiPrep}
                        variant="outline"
                        size="sm"
                      >
                        <Copy size={14} className="mr-1" />
                        Copy
                      </Button>
                    </div>
                  </div>
                  
                  <div className="prose prose-sm max-w-none text-slate-700">
                    <div className="whitespace-pre-wrap text-sm leading-relaxed">
                      {aiPrepContent || aiResponse}
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Compliance Tab */}
            <TabsContent value="compliance" className="space-y-4">
              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900 mb-4">
                  Compliance Review
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Compliance Score</p>
                    <div className="flex items-center gap-3 mt-2">
                      <div className={`w-16 h-16 flex items-center justify-center font-mono text-2xl font-bold ${
                        deal.compliance_score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                        deal.compliance_score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {deal.compliance_score}
                      </div>
                      <span className="text-sm text-slate-500">out of 100</span>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Compliance Status</p>
                    <Select
                      value={deal.compliance_status || 'green'}
                      onValueChange={(v) => handleStatusUpdate('compliance_status', v)}
                    >
                      <SelectTrigger className="w-36" data-testid="compliance-status-select">
                        <SelectValue>
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${
                            deal.compliance_status === 'green' ? 'bg-emerald-100 text-emerald-700' :
                            deal.compliance_status === 'yellow' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {(deal.compliance_status || 'green').toUpperCase()}
                          </span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="green">Green</SelectItem>
                        <SelectItem value="yellow">Yellow</SelectItem>
                        <SelectItem value="red">Red</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Language Compliance</p>
                    <Select
                      value={deal.language_compliance || 'English'}
                      onValueChange={(v) => handleStatusUpdate('language_compliance', v)}
                    >
                      <SelectTrigger className="w-36" data-testid="language-compliance-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="English">English</SelectItem>
                        <SelectItem value="Spanish">Spanish</SelectItem>
                        <SelectItem value="Bilingual">Bilingual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Missing Signatures</p>
                    {deal.missing_signatures?.length > 0 ? (
                      <ul className="mt-2 space-y-1">
                        {deal.missing_signatures.map((sig, idx) => (
                          <li key={idx} className="text-sm text-red-600 flex items-center gap-2">
                            <X size={14} />
                            {sig}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-emerald-600 flex items-center gap-2 mt-2">
                        <Check size={14} />
                        All signatures collected
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">10-Day Letter</p>
                    <Select
                      value={deal.ten_day_letter_sent ? 'sent' : 'not_sent'}
                      onValueChange={(v) => handleStatusUpdate('ten_day_letter_sent', v === 'sent')}
                    >
                      <SelectTrigger className="w-36" data-testid="ten-day-letter-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sent">Sent</SelectItem>
                        <SelectItem value="not_sent">Not Sent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* AI Compliance Check */}
                <div className="mt-6 pt-4 border-t border-slate-200">
                  <Button
                    onClick={() => handleAIAnalysis('compliance_check')}
                    disabled={aiLoading}
                    variant="outline"
                    className="w-full"
                    data-testid="ai-compliance-check"
                  >
                    {aiLoading ? (
                      <Loader2 size={16} className="mr-2 animate-spin" />
                    ) : (
                      <Shield size={16} className="mr-2" />
                    )}
                    AI Compliance Analysis
                  </Button>
                  {aiResponse && (
                    <div className="mt-4 p-4 bg-slate-50 border border-slate-200">
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{aiResponse}</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="activity" className="space-y-4">
              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900 mb-4">
                  Activity Log
                </h3>
                
                {activities.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No activity recorded</p>
                ) : (
                  <div className="space-y-4">
                    {activities.map((activity, idx) => (
                      <div key={idx} className="flex gap-4 p-3 border-l-2 border-slate-200 bg-slate-50">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 text-xs font-semibold uppercase ${
                              activity.category === 'ai' ? 'bg-purple-100 text-purple-700' :
                              activity.category === 'stip' ? 'bg-blue-100 text-blue-700' :
                              activity.category === 'interview' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {activity.category}
                            </span>
                            <span className="text-sm font-medium text-slate-900">{activity.action.replace(/_/g, ' ')}</span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{activity.details}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {activity.user_name} • {new Date(activity.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Stip Upload Modal */}
      <StipUploadModal
        open={uploadModal.open}
        onClose={() => setUploadModal({ open: false, stipName: '' })}
        stipName={uploadModal.stipName}
        dealId={id}
        onSuccess={handleUploadSuccess}
      />

      {/* Unwind Confirmation Modal */}
      {unwindModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={closeUnwindModal}
          data-testid="unwind-modal-backdrop"
        >
          <div
            className="bg-white w-full max-w-lg border border-slate-200 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="unwind-modal"
          >
            <div className="p-6 border-b border-slate-200 flex items-start gap-3">
              <XCircle size={22} className="text-red-600 mt-0.5" />
              <div>
                <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-900">
                  Unwind this deal?
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  Unwinding will remove this deal from the active pipeline and move it to Unwinds.
                  The deal will remain available for historical tracking.
                </p>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Unwind Reason *
                </Label>
                <Select value={unwindReason} onValueChange={setUnwindReason}>
                  <SelectTrigger className="mt-1" data-testid="unwind-reason-select">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Customer Canceled">Customer Canceled</SelectItem>
                    <SelectItem value="Financing Declined">Financing Declined</SelectItem>
                    <SelectItem value="Vehicle Returned">Vehicle Returned</SelectItem>
                    <SelectItem value="Lender Issue">Lender Issue</SelectItem>
                    <SelectItem value="Documentation Issue">Documentation Issue</SelectItem>
                    <SelectItem value="Customer Unable to Fund">Customer Unable to Fund</SelectItem>
                    <SelectItem value="Dealer Decision">Dealer Decision</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {unwindReason === 'Other' && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    Custom Reason *
                  </Label>
                  <Input
                    className="mt-1"
                    placeholder="Describe the unwind reason"
                    value={unwindReasonOther}
                    onChange={(e) => setUnwindReasonOther(e.target.value)}
                    data-testid="unwind-reason-other-input"
                  />
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3">
              <Button
                variant="ghost"
                onClick={closeUnwindModal}
                disabled={unwinding}
                data-testid="unwind-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmUnwind}
                disabled={unwinding || !unwindReason || (unwindReason === 'Other' && !unwindReasonOther.trim())}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="unwind-confirm-btn"
              >
                {unwinding ? (
                  <><Loader2 size={14} className="mr-1 animate-spin" /> Unwinding...</>
                ) : (
                  <><XCircle size={14} className="mr-1" /> Confirm Unwind</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
